import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  DateRange as DateIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const HTProductionReports = () => {
  const [reportType, setReportType] = useState('summary');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    furnace_code: '',
    ht_code: '',
    die_no: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  // Fetch HT Production Reports
  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['htProductionReports', reportType, page, rowsPerPage, filters],
    async () => {
      const params = new URLSearchParams({
        type: reportType,
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });

      const response = await api.get(`/heat-treatment/reports?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching reports: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Implementation for PDF download
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading HT production reports: {error.message}
        </Alert>
      </Box>
    );
  }

  const reportSummary = reportData?.data?.summary || {};
  const reportRecords = reportData?.data?.records || [];
  const pagination = reportData?.data?.pagination || {};

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Heat Treatment Production Reports
      </Typography>

      {/* Report Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                label="Report Type"
              >
                <MenuItem value="summary">Production Summary</MenuItem>
                <MenuItem value="detailed">Detailed Report</MenuItem>
                <MenuItem value="furnace">Furnace-wise</MenuItem>
                <MenuItem value="efficiency">Efficiency Report</MenuItem>
                <MenuItem value="rework">Rework Analysis</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Furnace Code"
              value={filters.furnace_code}
              onChange={(e) => setFilters({ ...filters, furnace_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="HT Code"
              value={filters.ht_code}
              onChange={(e) => setFilters({ ...filters, ht_code: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={() => refetch()}
              startIcon={<SearchIcon />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download PDF
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Report Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {reportSummary.totalBatches || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {reportSummary.totalQuantity || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Quantity
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {reportSummary.totalTonnage || '0.000'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Tonnage (MT)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {reportSummary.reworkBatches || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Rework Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Report Data Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                {reportType === 'summary' ? 'Production Summary Report' :
                 reportType === 'detailed' ? 'Detailed Production Report' :
                 reportType === 'furnace' ? 'Furnace-wise Production Report' :
                 reportType === 'efficiency' ? 'Efficiency Analysis Report' :
                 'Rework Analysis Report'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {reportType === 'summary' ? (
                      <>
                        <TableCell>Date</TableCell>
                        <TableCell>Batches</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Tonnage</TableCell>
                        <TableCell>Efficiency</TableCell>
                        <TableCell>Rework</TableCell>
                      </>
                    ) : reportType === 'furnace' ? (
                      <>
                        <TableCell>Furnace Code</TableCell>
                        <TableCell>Batches</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Tonnage</TableCell>
                        <TableCell>Utilization</TableCell>
                        <TableCell>Performance</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Date</TableCell>
                        <TableCell>Die No</TableCell>
                        <TableCell>HT Code</TableCell>
                        <TableCell>Furnace</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Tonnage</TableCell>
                        <TableCell>Status</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportRecords.map((record, index) => (
                      <TableRow key={index}>
                        {reportType === 'summary' ? (
                          <>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.batches}</TableCell>
                            <TableCell>{record.quantity}</TableCell>
                            <TableCell>{record.tonnage}</TableCell>
                            <TableCell>
                              <Chip 
                                label={`${record.efficiency || 0}%`} 
                                color={record.efficiency >= 90 ? 'success' : record.efficiency >= 70 ? 'warning' : 'error'} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>
                              {record.rework > 0 && (
                                <Chip label={record.rework} color="warning" size="small" />
                              )}
                            </TableCell>
                          </>
                        ) : reportType === 'furnace' ? (
                          <>
                            <TableCell>{record.furnace_code}</TableCell>
                            <TableCell>{record.batches}</TableCell>
                            <TableCell>{record.quantity}</TableCell>
                            <TableCell>{record.tonnage}</TableCell>
                            <TableCell>{record.utilization}%</TableCell>
                            <TableCell>
                              <Chip 
                                label={record.performance} 
                                color={record.performance === 'Good' ? 'success' : 'warning'} 
                                size="small" 
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.die_no}</TableCell>
                            <TableCell>{record.ht_code}</TableCell>
                            <TableCell>{record.furnace_code}</TableCell>
                            <TableCell>{record.quantity}</TableCell>
                            <TableCell>{record.tonnage}</TableCell>
                            <TableCell>
                              <Chip 
                                label={record.rework === 'Y' ? 'Rework' : 'OK'} 
                                color={record.rework === 'Y' ? 'warning' : 'success'} 
                                size="small" 
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pagination.total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                showFirstButton
                showLastButton
              />
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default HTProductionReports;